import {
  Alert,
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { cargoTypeInfo } from '../../pages/fulfillmentUi.mjs';

const POINT_LIST_CAP = 40;

function norm(value) {
  return String(value || '').trim().toLowerCase().replace(/ё/g, 'е');
}

function normPlace(value) {
  return norm(value).replace(/^(г|аул)\.?\s+/, '');
}

function shortenAddress(address, city) {
  let text = String(address || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  const prefixes = [
    /^россия[, ]+/i,
    /^рф[, ]+/i,
    /^республика адыгея(?:\s*\(адыгея\))?[, ]+/i,
    /^краснодарский край[, ]+/i,
    /^г\.?\s*о\.?\s*краснодар[, ]+/i,
    /^(?:г\.?\s*)?краснодар[, ]+/i,
    /^(?:аул\s+)?тахтамукай[, ]+/i,
  ];
  const cityText = String(city || '').trim();
  if (cityText) {
    const escaped = cityText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    prefixes.push(new RegExp(`^(?:г\\.?\\s*|аул\\s*)?${escaped}[, ]+`, 'i'));
  }
  for (let guard = 0; guard < 6; guard += 1) {
    const next = prefixes
      .reduce((acc, re) => acc.replace(re, ''), text)
      .replace(/^[, ]+/, '');
    if (next === text) break;
    text = next;
  }
  return text.trim();
}

function pointPrimary(point) {
  const name = String(point?.name || '').trim();
  const city = String(point?.city || '').trim();
  const address = String(point?.address || '').trim();
  if (name && normPlace(name) !== normPlace(city)) return name;
  return shortenAddress(address, city) || name || `Пункт ${point?.id ?? ''}`;
}

function pointInputLabel(point) {
  if (!point) return '';
  const type = point.office_type_label || '';
  const primary = pointPrimary(point);
  return type ? `${type} · ${primary}` : primary;
}

function pointSearchBlob(point) {
  return [
    point?.name,
    point?.address,
    point?.city,
    point?.office_type_label,
    pointPrimary(point),
  ].filter(Boolean).join(' ');
}

function pointRank(point) {
  const name = String(point?.name || '');
  if (point?.office_type === 'sc' && /тахтамукай/i.test(name) && !/тахтамукайск/i.test(name)) return 0;
  if (point?.office_type === 'sc') return 1;
  if (point?.office_type === 'sw') return 2;
  return 3;
}

function sortShippingPoints(points) {
  return [...(points || [])].sort((a, b) => {
    const diff = pointRank(a) - pointRank(b);
    if (diff) return diff;
    return pointPrimary(a).localeCompare(pointPrimary(b), 'ru');
  });
}

function filterShippingPoints(points, inputValue, selected) {
  const query = norm(inputValue);
  const selectedLabel = norm(pointInputLabel(selected));
  const browsing = !query || query === selectedLabel;
  const matched = browsing
    ? points
    : points.filter((point) => norm(pointSearchBlob(point)).includes(query));
  return matched.slice(0, POINT_LIST_CAP);
}

const popperSlot = {
  popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 1 } },
  listbox: { sx: { maxHeight: 320 } },
};

const SHIPPING_TYPE_OPTIONS = [
  { value: 'selfShipping', label: 'Самовывоз' },
  { value: 'transportCompany', label: 'Транспортная компания' },
];

function emptyForm() {
  return {
    skip_shipping: false,
    city: '',
    cities: [],
    shipping_dt: '',
    shipping_type: 'selfShipping',
    supplies: [],
  };
}

function pointStillValid(pointId, points) {
  const id = Number(pointId);
  return Number.isSafeInteger(id) && id > 0 && (points || []).some((row) => Number(row.id) === id);
}

export function normalizeShippingForm(data, { preserve } = {}) {
  if (!data) return emptyForm();
  if (data.skip_shipping || data.skipShipping) {
    return { ...emptyForm(), skip_shipping: true };
  }
  const rawSupplies = Array.isArray(data.supplies) && data.supplies.length
    ? data.supplies
    : [{
      supply_id: data.supply_id || data.supplyId || '',
      cargo_type: data.cargo_type,
      shipping_point_id: data.shipping_point_id,
      points: data.points || [],
    }];
  const preserveById = new Map(
    (preserve?.supplies || []).map((row) => [String(row.supply_id), row]),
  );
  const supplies = rawSupplies.map((row) => {
    const supplyId = String(row.supply_id || row.wb_supply_id || '').trim();
    const points = Array.isArray(row.points) ? row.points : [];
    const preserved = preserveById.get(supplyId);
    const preferred = preserved?.shipping_point_id ?? row.shipping_point_id;
    return {
      supply_id: supplyId,
      // До подтверждения погрузки поставки в WB ещё нет — в подписи остаётся склад.
      wb_supply_id: row.wb_supply_id || null,
      warehouse_id: row.warehouse_id ?? null,
      cargo_type: row.cargo_type ?? null,
      is_b2b: row.is_b2b == null ? null : Boolean(row.is_b2b),
      shipping_point_id: pointStillValid(preferred, points) ? Number(preferred) : (row.shipping_point_id || ''),
      points,
    };
  });
  return {
    skip_shipping: false,
    city: String(data.city || preserve?.city || '').trim(),
    cities: Array.isArray(data.cities) ? data.cities : [],
    shipping_dt: String(preserve?.shipping_dt || data.shipping_dt || '').trim(),
    shipping_type: String(preserve?.shipping_type || data.shipping_type || 'selfShipping').trim() || 'selfShipping',
    supplies,
  };
}

export function shippingFormReady(form) {
  if (!form) return false;
  if (form.skip_shipping) return true;
  if (!form.city || !form.shipping_dt) return false;
  if (!Array.isArray(form.supplies) || !form.supplies.length) return false;
  return form.supplies.every((row) => Number(row.shipping_point_id) > 0);
}

export function shippingConfirmBody(form) {
  if (!form || form.skip_shipping) return { skip_shipping: true };
  return {
    city: form.city,
    shipping_dt: form.shipping_dt,
    shipping_type: form.shipping_type || 'selfShipping',
    supplies: (form.supplies || []).map((row) => ({
      supply_id: row.supply_id,
      shipping_point_id: Number(row.shipping_point_id),
    })),
  };
}

export default function WbSupplyShippingFields({
  form,
  loading = false,
  error = '',
  onChange,
  onCityChange,
}) {
  if (!form || form.skip_shipping) return null;
  const many = (form.supplies || []).length > 1;

  const patch = (next) => onChange?.({ ...form, ...next });

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Autocomplete
        fullWidth
        disabled={loading}
        options={form.cities || []}
        value={form.city || null}
        onChange={(_, city) => onCityChange?.(city || '')}
        noOptionsText="Город не найден"
        slotProps={popperSlot}
        renderInput={(params) => (
          <TextField {...params} label="Город" placeholder="Поиск города" />
        )}
      />
      {(form.supplies || []).map((row, index) => {
        const cargo = cargoTypeInfo(row.cargo_type);
        const hint = row.wb_supply_id
          || (row.warehouse_id != null ? `склад ${row.warehouse_id}` : '');
        const b2bHint = row.is_b2b ? ', юрлица' : '';
        const label = many
          ? `Склад отгрузки (${cargo.label}${b2bHint}${hint ? `, ${hint}` : ''})`
          : `Склад отгрузки${row.is_b2b ? ' (юрлица)' : ''}`;
        const points = sortShippingPoints(row.points);
        const selected = points.find((point) => Number(point.id) === Number(row.shipping_point_id)) || null;
        const hiddenCount = Math.max(0, points.length - POINT_LIST_CAP);
        return (
          <Autocomplete
            key={row.supply_id || index}
            fullWidth
            disabled={loading}
            options={points}
            value={selected}
            onChange={(_, point) => {
              const next = (form.supplies || []).map((item, itemIndex) => (
                itemIndex === index
                  ? { ...item, shipping_point_id: point ? Number(point.id) : '' }
                  : item
              ));
              patch({ supplies: next });
            }}
            getOptionLabel={pointInputLabel}
            isOptionEqualToValue={(option, val) => Number(option?.id) === Number(val?.id)}
            filterOptions={(options, state) => filterShippingPoints(options, state.inputValue, selected)}
            noOptionsText="Ничего не найдено"
            selectOnFocus
            handleHomeEndKeys
            slotProps={popperSlot}
            renderOption={(props, point) => {
              const { key, ...optionProps } = props;
              const primary = pointInputLabel(point);
              const address = String(point.address || '').trim();
              const showAddress = address && norm(address) !== norm(pointPrimary(point));
              return (
                <Box
                  component="li"
                  key={key}
                  {...optionProps}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    height: 'auto',
                    width: '100%',
                    py: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.35 }}>
                    {primary}
                  </Typography>
                  {showAddress ? (
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
                      {address}
                    </Typography>
                  ) : null}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder="Улица или название"
                helperText={hiddenCount
                  ? `В списке ${points.length}. Введите улицу или название — сначала видны ${POINT_LIST_CAP}.`
                  : 'Можно искать по улице или названию'}
              />
            )}
          />
        );
      })}
      <TextField
        fullWidth
        type="date"
        label="Дата отгрузки"
        value={form.shipping_dt || ''}
        onChange={(event) => patch({ shipping_dt: event.target.value })}
        InputLabelProps={{ shrink: true }}
        disabled={loading}
      />
      <FormControl fullWidth disabled={loading}>
        <InputLabel id="wb-shipping-type-label">Способ</InputLabel>
        <Select
          labelId="wb-shipping-type-label"
          label="Способ"
          value={form.shipping_type || 'selfShipping'}
          onChange={(event) => patch({ shipping_type: event.target.value })}
        >
          {SHIPPING_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {loading ? (
        <Typography variant="caption" color="text.secondary">Загрузка пунктов отгрузки…</Typography>
      ) : null}
    </Stack>
  );
}
